/**
 * Inicio — punto de partida del día.
 *
 * Arriba: saludo, pantallas visitadas recientemente y accesos rápidos
 * filtrados por los permisos del rol (mismas reglas que el menú) y ordenados
 * por la frecuencia de uso de cada persona (App.js registra las visitas vía
 * lib/usoPantallas). Widgets personales: "Cobros de Hoy" (clientes con cuota
 * que vence hoy, alcance de sucursal decidido por el backend) para quien
 * cobra pagos, y "Mi Gestión de Cobros" (cartera, promesas, gestiones del
 * día) para gestores. Abajo, solo para administradores: los indicadores del
 * mes y las gráficas.
 *
 * Nota: la versión anterior leía usuario/clave/roleweb de localStorage, que
 * el login guarda vacíos, así que nunca cargaba datos (siempre $0.00). Ahora
 * usa las credenciales del contexto vía useApi.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { useApi, usePerfilSucursal } from "lib/api";
import { useMaterialUIController } from "context";
import { tieneAccesoEntrada } from "modules/menu/menuVisible";
import { obtenerUso } from "lib/usoPantallas";
import MiGestionCobros from "layouts/dashboard/components/MiGestionCobros";
import CobrosDeHoy from "layouts/dashboard/components/CobrosDeHoy";
import EmbarquesEnCurso from "layouts/dashboard/components/EmbarquesEnCurso";
import AvisoAutorizaciones from "components/autorizaciones/AvisoAutorizaciones";
import AvisoPedidosMatricula from "modules/matriculas/AvisoPedidosMatricula";
import { FP } from "components/financepro/tokens";

/** Pantallas candidatas a acceso rápido, en orden de frecuencia de uso.
 *  `keys` son los programas que dan acceso (mismas claves que routes.js). */
const ACCESOS = [
  {
    nombre: "Cobrar Pagos",
    descripcion: "Cobros y recibos",
    ruta: "/pagos/gestion",
    icono: "point_of_sale",
    keys: ["ares4010", "ares4015", "ares4030", "informepagos"],
  },
  {
    nombre: "Ventas",
    descripcion: "Facturar crédito o contado",
    ruta: "/facturacion/ventas",
    icono: "sell",
    keys: ["ares2010", "ares2015", "informemotoresfacturados"],
  },
  {
    nombre: "Cuadre de Caja",
    descripcion: "Cierre del día",
    ruta: "/facturacion/cuadres",
    icono: "fact_check",
    keys: ["ares7070", "cuadrecaja", "ares7075"],
  },
  {
    nombre: "Préstamos",
    descripcion: "Gestión y expedientes",
    ruta: "/prestamos/gestion",
    icono: "account_balance",
    keys: ["informeprestamos", "expediente"],
  },
  {
    nombre: "Iniciales",
    descripcion: "Cobro de iniciales",
    ruta: "/facturacion/iniciales",
    icono: "request_quote",
    keys: ["ares4020", "ares4025", "ares4035"],
  },
  {
    nombre: "Inventario",
    descripcion: "Informe de motores",
    ruta: "/inventario/informemotores",
    icono: "two_wheeler",
    keys: ["informemotores"],
  },
  {
    nombre: "Matrículas",
    descripcion: "Cobros de matrícula",
    ruta: "/matriculas/cobros",
    icono: "badge",
    keys: ["ares6010", "ares6040", "ares6020"],
  },
  {
    nombre: "Morosidad",
    descripcion: "Reporte de morosidad",
    ruta: "/prestamos/reportemorosidad",
    icono: "report_problem",
    keys: ["reportemorosidad"],
  },
];

const money = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

function haceTiempo(t) {
  const min = Math.max(1, Math.round((Date.now() - Number(t || 0)) / 60000));
  if (min < 60) return `hace ${min} min`;
  const horas = Math.round(min / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.round(horas / 24);
  return dias === 1 ? "ayer" : `hace ${dias} días`;
}

function AccesoRapido({ acceso, onClick }) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        height: "100%",
        borderRadius: 2.5,
        border: "1px solid #E5E9F0",
        boxShadow: FP.shadow.soft,
        cursor: "pointer",
        transition: "all .15s ease",
        "&:hover": { borderColor: FP.color.navy, transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.6, p: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${FP.color.navy}12`,
            color: FP.color.navy,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 24 }}>{acceso.icono}</Icon>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: FP.color.textPrimary }}>
            {acceso.nombre}
          </Typography>
          <Typography noWrap sx={{ fontSize: "0.76rem", color: FP.color.textSecondary }}>
            {acceso.descripcion}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { post } = useApi();
  const { esadmin } = usePerfilSucursal();
  const [controller] = useMaterialUIController();
  const { usuarioLog, roleWeb, roleDetalles } = controller;

  const [sales, setSales] = useState("");
  const [facturacionmes, setFacturacionmes] = useState(0);
  const [cantidadmotores, setCantidadmotores] = useState(0);
  const [financiado, setFinanciado] = useState(0);
  const [pagos, setPagos] = useState(0);
  const [incautos, setIncautos] = useState(0);
  const [gastos, setGastos] = useState(0);
  const [barchart, setBarchart] = useState("");
  const [ventas, setVentas] = useState("");

  const { visitas, recientes } = useMemo(() => obtenerUso(usuarioLog), [usuarioLog]);

  // Accesos permitidos, ordenados por cuánto los usa esta persona; los no
  // visitados conservan el orden declarado (frecuencia típica).
  const accesosVisibles = ACCESOS.filter((a) => tieneAccesoEntrada({ keys: a.keys }, roleDetalles))
    .map((a, i) => ({ ...a, orden: i, usos: visitas[a.ruta]?.n || 0 }))
    .sort((a, b) => b.usos - a.usos || a.orden - b.orden)
    .slice(0, 8);

  // Recientes: lo visitado que NO está ya como acceso rápido, re-filtrado
  // contra el rol vigente (las keys guardadas pudieron quedar obsoletas).
  const rutasAcceso = new Set(accesosVisibles.map((a) => a.ruta));
  const recientesVisibles = recientes
    .filter((r) => r?.ruta && r.ruta !== "/dashboard" && !rutasAcceso.has(r.ruta))
    .filter((r) => tieneAccesoEntrada({ keys: r.keys }, roleDetalles))
    .slice(0, 4);

  const fechaLarga = new Date().toLocaleDateString("es-DO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    if (!esadmin) return;
    let activo = true;
    post("/api/facturacionultimomes", { sucursal: " " }).then(({ ok, data }) => {
      if (!activo || !ok) return;
      setFacturacionmes(data.facturacion[0].precio == null ? 0 : data.facturacion[0].precio);
      setCantidadmotores(data.facturacion[0].cantidad == null ? 0 : data.facturacion[0].cantidad);
      setFinanciado(data.prestamo[0].valor == null ? 0 : data.prestamo[0].valor);
      setPagos(data.pagos[0].valor == null ? 0 : data.pagos[0].valor);
      setIncautos(data.incautos[0].valor == null ? 0 : data.incautos[0].valor);
      setGastos(data.gastos[0].valor == null ? 0 : data.gastos[0].valor);
      setBarchart({
        labels: data.datachart.label,
        datasets: { label: "Ventas", data: data.datachart.valor },
      });
      setSales({
        labels: data.mesvalor.label,
        datasets: { label: "Motores Facturados", data: data.mesvalor.valor },
      });
      setVentas({
        labels: data.pagosvalor.label,
        datasets: { label: "Pagos Mensuales", data: data.pagosvalor.valor },
      });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esadmin]);

  return (
    <MDBox sx={{ p: { xs: 2, md: 3 }, width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.3rem", md: "1.55rem" },
            color: FP.color.textPrimary,
          }}
        >
          Hola, {usuarioLog || "bienvenido"}
        </Typography>
        <Typography
          sx={{ color: FP.color.textSecondary, fontSize: "0.9rem", textTransform: "capitalize" }}
        >
          {fechaLarga}
        </Typography>
      </Box>

      {/* Solo AdmG: bandeja de autorizaciones (antes solo en el celular) */}
      <AvisoAutorizaciones />

      {/* Central de matrículas (flag modificar de pedidomatriculas) */}
      <AvisoPedidosMatricula />

      {recientesVisibles.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.8rem",
              letterSpacing: "0.08em",
              color: FP.color.textSecondary,
              mb: 1.2,
            }}
          >
            CONTINUAR DONDE QUEDASTE
          </Typography>
          <Grid container spacing={2}>
            {recientesVisibles.map((r) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={r.ruta}>
                <AccesoRapido
                  acceso={{ nombre: r.nombre, descripcion: haceTiempo(r.t), icono: r.icono }}
                  onClick={() => navigate(r.ruta)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {accesosVisibles.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.8rem",
              letterSpacing: "0.08em",
              color: FP.color.textSecondary,
              mb: 1.2,
            }}
          >
            ACCESOS RÁPIDOS
          </Typography>
          <Grid container spacing={2}>
            {accesosVisibles.map((a) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={a.ruta}>
                <AccesoRapido acceso={a} onClick={() => navigate(a.ruta)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {tieneAccesoEntrada(
        { keys: ["ares4010", "ares4015", "ares4030", "informepagos"] },
        roleDetalles
      ) && <CobrosDeHoy />}

      {tieneAccesoEntrada({ keys: ["gestioncobros"] }, roleDetalles) && <MiGestionCobros />}

      {tieneAccesoEntrada({ keys: ["importaciones"] }, roleDetalles) && <EmbarquesEnCurso />}

      {esadmin && (
        <>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.8rem",
              letterSpacing: "0.08em",
              color: FP.color.textSecondary,
              mb: 1.2,
            }}
          >
            INDICADORES DEL MES
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="dark"
                  icon="weekend"
                  title="Facturacion/Mes"
                  count={money(facturacionmes)}
                  percentage={{ color: "success", label: "Facturacion Ult. Mes" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  icon="leaderboard"
                  title="Motores Vendidos"
                  count={cantidadmotores}
                  percentage={{ color: "success", label: "Mes Actual" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="success"
                  icon="store"
                  title="Financiado"
                  count={money(financiado)}
                  percentage={{ color: "success", label: "Ultimo mes" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="primary"
                  icon="person_add"
                  title="Cobrados"
                  count={money(pagos)}
                  percentage={{ color: "success", label: "Ultimo mes" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="primary"
                  icon="no_crash"
                  title="Total Incautos"
                  count={money(incautos)}
                  percentage={{ color: "success", label: "Ultimo mes" }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="primary"
                  icon="money_off"
                  title="Total Gastos"
                  count={money(gastos)}
                  percentage={{ color: "success", label: "Ultimo mes" }}
                />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={3}>
                  <ReportsBarChart
                    color="info"
                    title="Cobros Ultima Semana"
                    description="Lunes a Viernes"
                    date={new Date().toDateString()}
                    chart={barchart}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="success"
                    title="Ventas Motores"
                    description="ultimo 6 meses"
                    date={new Date().toDateString()}
                    chart={sales}
                  />
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="dark"
                    title="Cobros Realizados"
                    description="Cobros ultimos 6 meses"
                    date={new Date().toDateString()}
                    chart={ventas}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
          {String(roleWeb || "").trim() === "AdmG" && (
            <MDBox>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => navigate("/inventario/dashaboard")}
              >
                Pantalla Completa
              </MDButton>
            </MDBox>
          )}
        </>
      )}
    </MDBox>
  );
}

export default Dashboard;
